
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Gauge, TimerOff, CalendarClock, Trash2, Zap } from 'lucide-react';

interface GovernanceKPIProps {
  inactivityStats: number;
  inactivityThreshold: number;
  averageInactivePeriod: number;
  recommendedPolicy: {
    id: number;
    name: string;
  };
  isInactivityCritical: boolean;
  onPolicyExecute: (e: React.MouseEvent, policyId?: number) => void;
}

const GovernanceKPISection = ({
  inactivityStats,
  inactivityThreshold,
  averageInactivePeriod,
  recommendedPolicy,
  isInactivityCritical,
  onPolicyExecute
}: GovernanceKPIProps) => {
  return (
    <div className="apple-kpi-section">
      <h3 className="apple-kpi-header">
        <Gauge className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
        Governance KPIs
      </h3>
      
      <div className="space-y-2.5">
        {/* Inactivity Stats */}
        <div className="apple-kpi-row">
          <div className="apple-kpi-label">
            <div className="bg-purple-500/20 apple-kpi-icon-container">
              <TimerOff className="h-3.5 w-3.5 text-purple-400" />
            </div>
            <span className="text-xs text-gray-300">Inactivity</span>
          </div>
          <div className="apple-kpi-value">
            <span className={`apple-kpi-primary ${isInactivityCritical ? 'apple-critical' : 'text-blue-400'}`}>
              {inactivityStats} days
            </span>
            <div className="apple-kpi-divider"></div>
            <span className="apple-kpi-secondary">
              Threshold: {inactivityThreshold}
            </span>
          </div>
        </div>
        
        {/* Average Inactive Period */}
        <div className="apple-kpi-row">
          <div className="apple-kpi-label">
            <div className="bg-blue-500/20 apple-kpi-icon-container">
              <CalendarClock className="h-3.5 w-3.5 text-blue-400" />
            </div>
            <span className="text-xs text-gray-300">Avg. Inactive</span>
          </div>
          <span className="apple-kpi-primary text-blue-400">
            {averageInactivePeriod} days
          </span>
        </div>
        
        {/* Recommended Policy */}
        <div className="apple-kpi-row">
          <div className="apple-kpi-label">
            <div className="bg-amber-500/20 apple-kpi-icon-container">
              <Trash2 className="h-3.5 w-3.5 text-amber-400" />
            </div>
            <span className="text-xs text-gray-300">Recommended</span>
          </div>
          <div className="flex items-center">
            <Badge 
              className="bg-gradient-to-r from-amber-500/80 to-amber-600/80 apple-policy-badge border-0"
              onClick={(e) => {
                e.stopPropagation();
                onPolicyExecute(e, recommendedPolicy.id);
              }}
            >
              {recommendedPolicy.name}
            </Badge>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="apple-action-button bg-gray-700/50"
                    onClick={(e) => onPolicyExecute(e, recommendedPolicy.id)}
                  >
                    <Zap className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Execute {recommendedPolicy.name} policy</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GovernanceKPISection;
